// ==================== CONTACTS PAGE ====================

import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Users,
  Plus,
  Search,
  Filter,
  Download,
  Upload,
  Trash2,
  Edit,
  Phone,
  Mail,
  Building,
  Globe,
  Tag,
  CheckCircle,
  XCircle,
  UserX,
  UserCheck,
  FolderPlus,
  X,
  Loader2,
  MessageSquare,
  Smartphone,
} from 'lucide-react';
import { Button, Input, Badge, Card, Checkbox } from '../../../components/ui';
import { Dropdown, type DropdownItem } from '../../../components/ui/Dropdown';
import { ConfirmDialog } from '../../../components/ui/ConfirmDialog';
import { Pagination } from '../../../components/ui/Pagination';
import { APP_CONSTANTS, ROUTES } from '../../../constants';
import { contactsService, type QueryContactsParams } from '../../../services/contacts.service';
import { useOrganizations } from '../../../hooks/useOrganizations';
import type { Contact, ContactGroup, ContactStats } from '../../../types';
import { getCountryByCode } from '../../../constants/countries.constants';
import ContactFormModal from './ContactFormModal';
import ImportContactsModal from './ImportContactsModal';
import ExportContactsModal from './ExportContactsModal';

// ==================== HELPER FUNCTIONS ====================

const formatPhoneNumber = (phone: string): string => {
  // Add + if not present and format nicely
  const cleaned = phone.replace(/\D/g, '');
  if (cleaned.length === 10) {
    return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
  }
  return phone.startsWith('+') ? phone : `+${phone}`;
};

const formatDate = (dateString: string): string => {
  return new Date(dateString).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

// ==================== TAGS POPOVER COMPONENT ====================

interface TagsPopoverProps {
  tags: string[];
  maxVisible?: number;
}

const TagsPopover = ({ tags, maxVisible = 2 }: TagsPopoverProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const visibleTags = tags.slice(0, maxVisible);
  const hiddenTags = tags.slice(maxVisible);
  const hasMore = hiddenTags.length > 0;

  return (
    <div className="flex flex-wrap gap-1 items-center">
      {visibleTags.map((tag) => (
        <Badge key={tag} variant="default" size="sm">
          {tag}
        </Badge>
      ))}
      {hasMore && (
        <div className="relative">
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="inline-flex"
          >
            <Badge variant="info" size="sm" className="cursor-pointer hover:opacity-80">
              +{hiddenTags.length}
            </Badge>
          </button>
          
          <AnimatePresence>
            {isOpen && (
              <>
                {/* Backdrop */}
                <div 
                  className="fixed inset-0 z-40" 
                  onClick={() => setIsOpen(false)}
                />
                {/* Popover */}
                <motion.div
                  initial={{ opacity: 0, scale: 0.95, y: -5 }}
                  animate={{ opacity: 1, scale: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.95, y: -5 }}
                  transition={{ duration: 0.15 }}
                  className="absolute left-0 top-full mt-1 z-50 bg-[#1A2332] border border-white/10 rounded-lg shadow-xl p-3 min-w-[200px] max-w-[300px]"
                >
                  <div className="flex items-center justify-between mb-2 pb-2 border-b border-white/10">
                    <span className="text-xs font-medium text-gray-400">All Tags ({tags.length})</span>
                    <button 
                      onClick={() => setIsOpen(false)}
                      className="text-gray-400 hover:text-white"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                  <div className="flex flex-wrap gap-1 max-h-[150px] overflow-y-auto">
                    {tags.map((tag) => (
                      <Badge key={tag} variant="default" size="sm">
                        {tag}
                      </Badge>
                    ))}
                  </div>
                </motion.div>
              </>
            )}
          </AnimatePresence>
        </div>
      )}
    </div>
  );
};

// ==================== CONTACT ROW COMPONENT ====================

interface ContactRowProps {
  contact: Contact;
  isSelected: boolean;
  onSelect: (id: string) => void;
  onEdit: (contact: Contact) => void;
  onDelete: (contact: Contact) => void;
  onToggleOptIn: (contact: Contact) => void;
  onSendWhatsApp: (contact: Contact) => void;
  onSendSms: (contact: Contact) => void;
}

const ContactRow = ({
  contact,
  isSelected,
  onSelect,
  onEdit,
  onDelete,
  onToggleOptIn,
  onSendWhatsApp,
  onSendSms,
}: ContactRowProps) => {
  const dropdownItems: DropdownItem[] = [
    {
      key: 'send-whatsapp',
      label: 'Send WhatsApp',
      icon: <MessageSquare className="w-4 h-4" />,
      onClick: () => onSendWhatsApp(contact),
    },
    {
      key: 'send-sms',
      label: 'Send SMS',
      icon: <Smartphone className="w-4 h-4" />,
      onClick: () => onSendSms(contact),
    },
    {
      key: 'edit',
      label: 'Edit Contact',
      icon: <Edit className="w-4 h-4" />,
      onClick: () => onEdit(contact),
    },
    {
      key: 'toggle-opt',
      label: contact.optedIn ? 'Mark Opted Out' : 'Mark Opted In',
      icon: contact.optedIn ? <UserX className="w-4 h-4" /> : <UserCheck className="w-4 h-4" />,
      onClick: () => onToggleOptIn(contact),
    },
    {
      key: 'delete',
      label: 'Delete Contact',
      icon: <Trash2 className="w-4 h-4" />,
      colorClass: 'text-red-400 hover:text-red-300',
      onClick: () => onDelete(contact),
    },
  ];

  return (
    <motion.tr
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className={`border-b border-white/5 hover:bg-white/5 transition-colors ${
        isSelected ? 'bg-primary-500/10' : ''
      }`}
    >
      {/* Checkbox */}
      <td className="px-4 py-3">
        <Checkbox
          checked={isSelected}
          onChange={() => onSelect(contact.id)}
          size="sm"
        />
      </td>

      {/* Contact Info */}
      <td className="px-4 py-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-linear-to-br from-primary-500 to-primary-600 flex items-center justify-center text-white font-medium">
            {contact.name ? contact.name.charAt(0).toUpperCase() : contact.phoneNumber.slice(-2)}
          </div>
          <div>
            <div className="font-medium text-white">
              {contact.name || 'Unknown'}
            </div>
            <div className="text-sm text-gray-400 flex items-center gap-1">
              <Phone className="w-3 h-3" />
              {formatPhoneNumber(contact.phoneNumber)}
            </div>
          </div>
        </div>
      </td>

      {/* Email */}
      <td className="px-4 py-3">
        {contact.email ? (
          <div className="flex items-center gap-1 text-gray-300">
            <Mail className="w-4 h-4 text-gray-500" />
            <span className="truncate max-w-[180px]">{contact.email}</span>
          </div>
        ) : (
          <span className="text-gray-500">—</span>
        )}
      </td>

      {/* Company */}
      <td className="px-4 py-3">
        {contact.company ? (
          <div className="flex items-center gap-1 text-gray-300">
            <Building className="w-4 h-4 text-gray-500" />
            <span className="truncate max-w-[150px]">{contact.company}</span>
          </div>
        ) : (
          <span className="text-gray-500">—</span>
        )}
      </td>

      {/* Country */}
      <td className="px-4 py-3">
        {contact.country ? (
          <div className="flex items-center gap-1.5 text-gray-300">
            <span className="text-lg">{getCountryByCode(contact.country)?.flag || '🏳️'}</span>
            <span className="truncate max-w-[120px]">
              {getCountryByCode(contact.country)?.name || contact.country}
            </span>
          </div>
        ) : (
          <span className="text-gray-500">—</span>
        )}
      </td>

      {/* Tags */}
      <td className="px-4 py-3">
        {contact.tags.length > 0 ? (
          <TagsPopover tags={contact.tags} maxVisible={2} />
        ) : (
          <span className="text-gray-500">—</span>
        )}
      </td>

      {/* Groups */}
      <td className="px-4 py-3">
        <div className="flex flex-wrap gap-1">
          {contact.groups?.slice(0, 2).map((g) => (
            <Badge
              key={g.group.id}
              size="sm"
              variant="info"
              className={g.group.color ? `!bg-[${g.group.color}20] !text-[${g.group.color}]` : undefined}
            >
              {g.group.name}
            </Badge>
          ))}
          {(contact.groups?.length || 0) > 2 && (
            <Badge variant="info" size="sm">
              +{(contact.groups?.length || 0) - 2}
            </Badge>
          )}
        </div>
      </td>

      {/* Status */}
      <td className="px-4 py-3">
        {contact.optedIn ? (
          <Badge variant="success" size="sm" className="flex items-center gap-1 w-fit whitespace-nowrap">
            <CheckCircle className="w-3 h-3 shrink-0" />
            <span>Opted In</span>
          </Badge>
        ) : (
          <Badge variant="error" size="sm" className="flex items-center gap-1 w-fit whitespace-nowrap">
            <XCircle className="w-3 h-3 shrink-0" />
            <span>Opted Out</span>
          </Badge>
        )}
      </td>

      {/* Created */}
      <td className="px-4 py-3 text-gray-400 text-sm">
        {formatDate(contact.createdAt)}
      </td>

      {/* Actions */}
      <td className="px-4 py-3">
        <Dropdown items={dropdownItems} menuWidth={180} />
      </td>
    </motion.tr>
  );
};

// ==================== STATS CARDS ====================

interface StatsCardsProps {
  stats: ContactStats | null;
  loading: boolean;
}

const StatsCards = ({ stats, loading }: StatsCardsProps) => {
  if (loading || !stats) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        {[...Array(4)].map((_, i) => (
          <div key={i} className="h-24 bg-white/5 rounded-xl animate-pulse" />
        ))}
      </div>
    );
  }

  const cards = [
    {
      label: 'Total Contacts',
      value: stats.total.toLocaleString(),
      icon: Users,
      color: 'text-primary-400',
      bg: 'bg-primary-500/10',
    },
    {
      label: 'Opted In',
      value: stats.optedIn.toLocaleString(),
      icon: CheckCircle,
      color: 'text-green-400',
      bg: 'bg-green-500/10',
    },
    {
      label: 'Opted Out',
      value: stats.optedOut.toLocaleString(),
      icon: XCircle,
      color: 'text-red-400',
      bg: 'bg-red-500/10',
    },
    {
      label: 'Groups',
      value: stats.byGroup.length.toString(),
      icon: FolderPlus,
      color: 'text-purple-400',
      bg: 'bg-purple-500/10',
    },
  ];

  return (
    <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
      {cards.map((card, i) => (
        <motion.div
          key={card.label}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: i * 0.1 }}
        >
          <Card className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-400">{card.label}</p>
                <p className="text-2xl font-bold text-white mt-1">{card.value}</p>
              </div>
              <div className={`p-3 rounded-xl ${card.bg}`}>
                <card.icon className={`w-6 h-6 ${card.color}`} />
              </div>
            </div>
          </Card>
        </motion.div>
      ))}
    </div>
  );
};

// ==================== MAIN CONTACTS PAGE ====================

const ContactsPage = () => {
  const navigate = useNavigate();
  
  // State
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [groups, setGroups] = useState<ContactGroup[]>([]);
  const [stats, setStats] = useState<ContactStats | null>(null);
  const [allTags, setAllTags] = useState<string[]>([]);
  
  const [loading, setLoading] = useState(true);
  const [statsLoading, setStatsLoading] = useState(true);
  
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [selectedGroupIds, setSelectedGroupIds] = useState<string[]>([]);
  const [optedInFilter, setOptedInFilter] = useState<boolean | undefined>(undefined);
  const [sortBy] = useState<'createdAt' | 'name'>('createdAt');
  const [sortOrder] = useState<'asc' | 'desc'>('desc');
  
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState<number>(APP_CONSTANTS.DEFAULT_PAGE_SIZE);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  
  const [selectedContacts, setSelectedContacts] = useState<Set<string>>(new Set());
  const [showFilters, setShowFilters] = useState(false);

  // Contacts and tags are shared; only the groups (filter and Groups count) follow the header account.
  const { selectedOrganizationId } = useOrganizations();
  const [listOrganizationId, setListOrganizationId] = useState(selectedOrganizationId);
  if (listOrganizationId !== selectedOrganizationId) {
    setListOrganizationId(selectedOrganizationId);
    setSelectedGroupIds([]);
    if (selectedGroupIds.length) setPage(1);
  }
  
  const [showFormModal, setShowFormModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [editingContact, setEditingContact] = useState<Contact | null>(null);
  const [contactToDelete, setContactToDelete] = useState<Contact | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showBulkDeleteConfirm, setShowBulkDeleteConfirm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [bulkDeleteResult, setBulkDeleteResult] = useState<{ deleted: number; skipped: number; errors: string[] } | null>(null);
  
  // Opt-in/out toggle confirmation
  const [contactToToggle, setContactToToggle] = useState<Contact | null>(null);
  const [showOptToggleConfirm, setShowOptToggleConfirm] = useState(false);
  const [isTogglingOpt, setIsTogglingOpt] = useState(false);

  // ==================== DATA FETCHING ====================

  const fetchContacts = useCallback(async () => {
    setLoading(true);
    try {
      const params: QueryContactsParams = {
        search: searchQuery || undefined,
        tags: selectedTags.length > 0 ? selectedTags : undefined,
        groupIds: selectedGroupIds.length > 0 ? selectedGroupIds : undefined,
        optedIn: optedInFilter,
        sortBy,
        sortOrder,
        page,
        limit: pageSize,
      };

      const response = await contactsService.getAll(params);
      setContacts(response?.items || []);
      setTotalPages(response?.meta?.totalPages || 1);
      setTotal(response?.meta?.total || 0);
    } catch (error) {
      console.error('Failed to fetch contacts:', error);
      setContacts([]);
      setTotalPages(1);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [searchQuery, selectedTags, selectedGroupIds, optedInFilter, sortBy, sortOrder, page, pageSize]);

  const fetchStats = useCallback(async () => {
    setStatsLoading(true);
    try {
      const data = await contactsService.getStats(selectedOrganizationId);
      setStats(data || { total: 0, optedIn: 0, optedOut: 0, byGroup: [] });
    } catch (error) {
      console.error('Failed to fetch stats:', error);
      setStats({ total: 0, optedIn: 0, optedOut: 0, byGroup: [] });
    } finally {
      setStatsLoading(false);
    }
  }, [selectedOrganizationId]);

  const fetchGroups = useCallback(async () => {
    try {
      // Fetch only active groups for selection (filters, contact form)
      const data = await contactsService.getActiveGroups(selectedOrganizationId);
      setGroups(data || []);
    } catch (error) {
      console.error('Failed to fetch groups:', error);
      setGroups([]);
    }
  }, [selectedOrganizationId]);

  const fetchTags = useCallback(async () => {
    try {
      const data = await contactsService.getAllTags();
      setAllTags(data || []);
    } catch (error) {
      console.error('Failed to fetch tags:', error);
      setAllTags([]);
    }
  }, []);

  useEffect(() => {
    fetchContacts();
  }, [fetchContacts]);

  useEffect(() => {
    fetchStats();
    fetchGroups();
    fetchTags();
  }, [fetchStats, fetchGroups, fetchTags]);

  // ==================== HANDLERS ====================

  const handleSelectContact = (id: string) => {
    const newSelected = new Set(selectedContacts);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedContacts(newSelected);
  };

  const handleSelectAll = () => {
    if (selectedContacts.size === contacts.length) {
      setSelectedContacts(new Set());
    } else {
      setSelectedContacts(new Set(contacts.map((c) => c.id)));
    }
  };

  const handleEditContact = (contact: Contact) => {
    setEditingContact(contact);
    setShowFormModal(true);
  };

  // New contacts are shared; the selected account supplies groups and fields
  const handleAddContact = () => {
    setEditingContact(null);
    setShowFormModal(true);
  };

  const handleSendWhatsApp = (contact: Contact) => {
    // Navigate to WhatsApp messages page with contact ID as query parameter
    navigate(`${ROUTES.CUSTOMER.MESSAGES}?contactId=${contact.id}`);
  };

  const handleSendSms = (contact: Contact) => {
    // Navigate to SMS messages page with contact ID as query parameter
    navigate(`${ROUTES.CUSTOMER.SMS_MESSAGES}?contactId=${contact.id}`);
  };

  const handleDeleteContact = (contact: Contact) => {
    setContactToDelete(contact);
    setDeleteError(null);
    setShowDeleteConfirm(true);
  };
  
  const handleBulkDeleteClick = () => {
    setDeleteError(null);
    setBulkDeleteResult(null);
    setShowBulkDeleteConfirm(true);
  };

  const confirmDelete = async () => {
    if (!contactToDelete) return;
    
    setIsDeleting(true);
    setDeleteError(null);
    
    try {
      await contactsService.delete(contactToDelete.id);
      await fetchContacts();
      await fetchStats();
      setShowDeleteConfirm(false);
      setContactToDelete(null);
    } catch (error: unknown) {
      // Extract error message from API response
      let errorMessage = 'Failed to delete contact. It may have associated messages.';
      
      if (error && typeof error === 'object') {
        const errorObj = error as Record<string, unknown>;
        if (typeof errorObj.message === 'string') {
          errorMessage = errorObj.message;
        }
      }
      
      setDeleteError(errorMessage);
      setIsDeleting(false);
      return;
    }
    setIsDeleting(false);
  };

  const confirmBulkDelete = async () => {
    // If we already have results and user clicks again, just close
    if (bulkDeleteResult) {
      setShowBulkDeleteConfirm(false);
      setBulkDeleteResult(null);
      setDeleteError(null);
      return;
    }
    
    setIsDeleting(true);
    setDeleteError(null);
    setBulkDeleteResult(null);
    try {
      const result = await contactsService.bulkDelete(Array.from(selectedContacts));
      setBulkDeleteResult(result);
      
      if (result.deleted > 0) {
        await fetchContacts();
        await fetchStats();
        setSelectedContacts(new Set());
      }
      
      // If all were deleted successfully, close the dialog
      if (result.skipped === 0) {
        setShowBulkDeleteConfirm(false);
        setBulkDeleteResult(null);
      }
    } catch (error: unknown) {
      console.error('Failed to bulk delete contacts:', error);
      // Extract error message from API response (interceptor transforms it to { message: "..." })
      const errorMessage = (error as { message?: string })?.message 
        || 'Failed to delete contacts.';
      setDeleteError(errorMessage);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleToggleOptIn = (contact: Contact) => {
    setContactToToggle(contact);
    setShowOptToggleConfirm(true);
  };

  const confirmToggleOptIn = async () => {
    if (!contactToToggle) return;
    
    setIsTogglingOpt(true);
    try {
      if (contactToToggle.optedIn) {
        await contactsService.optOut(contactToToggle.id);
      } else {
        await contactsService.optIn(contactToToggle.id);
      }
      await fetchContacts();
      await fetchStats();
      setShowOptToggleConfirm(false);
      setContactToToggle(null);
    } catch (error) {
      console.error('Failed to toggle opt-in status:', error);
    } finally {
      setIsTogglingOpt(false);
    }
  };

  const handleFormSuccess = () => {
    setShowFormModal(false);
    setEditingContact(null);
    fetchContacts();
    fetchStats();
    fetchTags();
  };


  // ==================== RENDER ====================

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-heading font-bold text-white flex items-center gap-3">
            <Users className="w-8 h-8 text-primary-400" />
            Contacts
          </h1>
          <p className="text-gray-400 mt-1">
            Manage your WhatsApp contacts and groups
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Button
            variant="secondary"
            onClick={() => setShowExportModal(true)}
            className="flex items-center gap-2"
          >
            <Download className="w-4 h-4" />
            Export
          </Button>
          <Button
            variant="secondary"
            onClick={() => {
              setShowImportModal(true);
            }}
            className="flex items-center gap-2"
          >
            <Upload className="w-4 h-4" />
            Import
          </Button>
          <Button
            onClick={handleAddContact}
            className="flex items-center gap-2"
          >
            <Plus className="w-4 h-4" />
            Add Contact
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <StatsCards stats={stats} loading={statsLoading} />

      {/* Filters Bar */}
      <Card className="p-4 mb-6">
        <div className="flex flex-wrap items-center gap-4">
          {/* Search */}
          <div className="flex-1 min-w-[250px]">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                type="text"
                placeholder="Search by name, phone, email..."
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setPage(1);
                }}
                className="pl-10"
              />
            </div>
          </div>

          {/* Filter Toggle */}
          <Button
            variant={showFilters ? 'primary' : 'secondary'}
            onClick={() => setShowFilters(!showFilters)}
            className="flex items-center gap-2"
          >
            <Filter className="w-4 h-4" />
            Filters
            {(selectedTags.length > 0 || selectedGroupIds.length > 0 || optedInFilter !== undefined) && (
              <Badge variant="orange" size="sm">
                {selectedTags.length + selectedGroupIds.length + (optedInFilter !== undefined ? 1 : 0)}
              </Badge>
            )}
          </Button>

          {/* Bulk Actions */}
          {selectedContacts.size > 0 && (
            <div className="flex items-center gap-2 ml-auto">
              <span className="text-sm text-gray-400">
                {selectedContacts.size} selected
              </span>
              <Button
                variant="danger"
                size="sm"
                onClick={handleBulkDeleteClick}
              >
                <Trash2 className="w-4 h-4 mr-1" />
                Delete
              </Button>
            </div>
          )}
        </div>

        {/* Expanded Filters */}
        <AnimatePresence>
          {showFilters && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              className="overflow-hidden"
            >
              <div className="pt-4 mt-4 border-t border-white/10 grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* Tags Filter */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Tags</label>
                  <div className="flex flex-wrap gap-1 max-h-24 overflow-y-auto p-2 bg-white/5 rounded-lg">
                    {allTags.map((tag) => (
                      <button
                        key={tag}
                        onClick={() => {
                          if (selectedTags.includes(tag)) {
                            setSelectedTags(selectedTags.filter((t) => t !== tag));
                          } else {
                            setSelectedTags([...selectedTags, tag]);
                          }
                          setPage(1);
                        }}
                        className={`px-2 py-1 text-xs rounded-full transition-colors ${
                          selectedTags.includes(tag)
                            ? 'bg-primary-500 text-white'
                            : 'bg-white/10 text-gray-300 hover:bg-white/20'
                        }`}
                      >
                        {tag}
                      </button>
                    ))}
                    {allTags.length === 0 && (
                      <span className="text-sm text-gray-500">No tags available</span>
                    )}
                  </div>
                </div>

                {/* Groups Filter */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Groups</label>
                  <div className="flex flex-wrap gap-1 max-h-24 overflow-y-auto p-2 bg-white/5 rounded-lg">
                    {groups.map((group) => (
                      <button
                        key={group.id}
                        onClick={() => {
                          if (selectedGroupIds.includes(group.id)) {
                            setSelectedGroupIds(selectedGroupIds.filter((g) => g !== group.id));
                          } else {
                            setSelectedGroupIds([...selectedGroupIds, group.id]);
                          }
                          setPage(1);
                        }}
                        className={`px-2 py-1 text-xs rounded-full transition-colors ${
                          selectedGroupIds.includes(group.id)
                            ? 'bg-primary-500 text-white'
                            : 'bg-white/10 text-gray-300 hover:bg-white/20'
                        }`}
                        style={{
                          backgroundColor: selectedGroupIds.includes(group.id)
                            ? group.color || undefined
                            : undefined,
                        }}
                      >
                        {group.name}
                      </button>
                    ))}
                    {groups.length === 0 && (
                      <span className="text-sm text-gray-500">No groups available</span>
                    )}
                  </div>
                </div>

                {/* Status Filter */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Status</label>
                  <div className="flex gap-2">
                    <button
                      onClick={() => {
                        setOptedInFilter(undefined);
                        setPage(1);
                      }}
                      className={`px-3 py-2 text-sm rounded-lg transition-colors ${
                        optedInFilter === undefined
                          ? 'bg-primary-500 text-white'
                          : 'bg-white/10 text-gray-300 hover:bg-white/20'
                      }`}
                    >
                      All
                    </button>
                    <button
                      onClick={() => {
                        setOptedInFilter(true);
                        setPage(1);
                      }}
                      className={`px-3 py-2 text-sm rounded-lg transition-colors flex items-center gap-1 ${
                        optedInFilter === true
                          ? 'bg-green-500 text-white'
                          : 'bg-white/10 text-gray-300 hover:bg-white/20'
                      }`}
                    >
                      <CheckCircle className="w-4 h-4" />
                      Opted In
                    </button>
                    <button
                      onClick={() => {
                        setOptedInFilter(false);
                        setPage(1);
                      }}
                      className={`px-3 py-2 text-sm rounded-lg transition-colors flex items-center gap-1 ${
                        optedInFilter === false
                          ? 'bg-red-500 text-white'
                          : 'bg-white/10 text-gray-300 hover:bg-white/20'
                      }`}
                    >
                      <XCircle className="w-4 h-4" />
                      Opted Out
                    </button>
                  </div>
                </div>
              </div>

              {/* Clear Filters */}
              {(selectedTags.length > 0 || selectedGroupIds.length > 0 || optedInFilter !== undefined) && (
                <div className="mt-4 flex justify-end">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setSelectedTags([]);
                      setSelectedGroupIds([]);
                      setOptedInFilter(undefined);
                      setPage(1);
                    }}
                    className="text-gray-400 hover:text-white"
                  >
                    <X className="w-4 h-4 mr-1" />
                    Clear all filters
                  </Button>
                </div>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </Card>

      {/* Contacts Table */}
      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-white/5 border-b border-white/10">
              <tr>
                <th className="px-4 py-3 text-left">
                  <Checkbox
                    checked={contacts.length > 0 && selectedContacts.size === contacts.length}
                    indeterminate={selectedContacts.size > 0 && selectedContacts.size < contacts.length}
                    onChange={handleSelectAll}
                    size="sm"
                  />
                </th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-400">
                  Contact
                </th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-400">
                  Email
                </th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-400">
                  Company
                </th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-400">
                  <div className="flex items-center gap-1">
                    <Globe className="w-4 h-4" />
                    Country
                  </div>
                </th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-400">
                  <div className="flex items-center gap-1">
                    <Tag className="w-4 h-4" />
                    Tags
                  </div>
                </th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-400">
                  Groups
                </th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-400">
                  Status
                </th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-400">
                  Created
                </th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-400">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={10} className="px-4 py-12 text-center">
                    <Loader2 className="w-8 h-8 animate-spin text-primary-500 mx-auto" />
                    <p className="text-gray-400 mt-2">Loading contacts...</p>
                  </td>
                </tr>
              ) : contacts.length === 0 ? (
                <tr>
                  <td colSpan={10} className="px-4 py-12 text-center">
                    <Users className="w-12 h-12 text-gray-600 mx-auto mb-3" />
                    <p className="text-gray-400">No contacts found</p>
                    <Button
                      variant="primary"
                      size="sm"
                      onClick={handleAddContact}
                      className="mt-4"
                    >
                      <Plus className="w-4 h-4 mr-1" />
                      Add your first contact
                    </Button>
                  </td>
                </tr>
              ) : (
                contacts.map((contact) => (
                  <ContactRow
                    key={contact.id}
                    contact={contact}
                    isSelected={selectedContacts.has(contact.id)}
                    onSelect={handleSelectContact}
                    onEdit={handleEditContact}
                    onDelete={handleDeleteContact}
                    onToggleOptIn={handleToggleOptIn}
                    onSendWhatsApp={handleSendWhatsApp}
                    onSendSms={handleSendSms}
                  />
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <Pagination
          currentPage={page}
          totalPages={totalPages}
          totalItems={total}
          pageSize={pageSize}
          isLoading={loading}
          onPageChange={setPage}
          onPageSizeChange={(size) => {
            setPageSize(size);
            setPage(1); // Reset to first page when page size changes
          }}
          itemLabel="contacts"
        />
      </Card>

      {/* Contact Form Modal */}
      {showFormModal && (
        <ContactFormModal
          contact={editingContact}
          groups={groups}
          onClose={() => {
            setShowFormModal(false);
            setEditingContact(null);
          }}
          onSuccess={handleFormSuccess}
        />
      )}

      {/* Delete Confirmation */}
      <ConfirmDialog
        isOpen={showDeleteConfirm}
        onClose={() => {
          setShowDeleteConfirm(false);
          setContactToDelete(null);
          setDeleteError(null);
        }}
        onConfirm={confirmDelete}
        title="Delete Contact"
        description={
          <div className="space-y-3">
            <p>Are you sure you want to delete {contactToDelete?.name || contactToDelete?.phoneNumber}? This action cannot be undone.</p>
            {deleteError && (
              <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 text-left">
                <p className="text-red-400 text-sm">{deleteError}</p>
              </div>
            )}
          </div>
        }
        confirmText="Delete"
        variant="danger"
        isLoading={isDeleting}
      />

      {/* Bulk Delete Confirmation */}
      <ConfirmDialog
        isOpen={showBulkDeleteConfirm}
        onClose={() => {
          setShowBulkDeleteConfirm(false);
          setDeleteError(null);
          setBulkDeleteResult(null);
        }}
        onConfirm={confirmBulkDelete}
        title="Delete Contacts"
        description={
          <div className="space-y-3">
            {!bulkDeleteResult ? (
              <p>Are you sure you want to delete {selectedContacts.size} contacts? This action cannot be undone.</p>
            ) : (
              <>
                {bulkDeleteResult.deleted > 0 && (
                  <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-3 text-left">
                    <p className="text-green-400 text-sm">
                      Successfully deleted {bulkDeleteResult.deleted} contact{bulkDeleteResult.deleted === 1 ? '' : 's'}.
                    </p>
                  </div>
                )}
                {bulkDeleteResult.skipped > 0 && (
                  <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3 text-left">
                    <p className="text-amber-400 text-sm font-medium mb-2">
                      {bulkDeleteResult.skipped} contact{bulkDeleteResult.skipped === 1 ? '' : 's'} could not be deleted (have message history):
                    </p>
                    <ul className="text-amber-400/80 text-xs space-y-1 max-h-24 overflow-y-auto">
                      {bulkDeleteResult.errors.slice(0, 5).map((err, i) => (
                        <li key={i}>• {err}</li>
                      ))}
                      {bulkDeleteResult.errors.length > 5 && (
                        <li>• ...and {bulkDeleteResult.errors.length - 5} more</li>
                      )}
                    </ul>
                  </div>
                )}
              </>
            )}
            {deleteError && (
              <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 text-left">
                <p className="text-red-400 text-sm">{deleteError}</p>
              </div>
            )}
          </div>
        }
        confirmText={bulkDeleteResult ? "Close" : "Delete All"}
        cancelText={bulkDeleteResult ? "Done" : "Cancel"}
        variant={bulkDeleteResult?.skipped ? "warning" : "danger"}
        isLoading={isDeleting}
      />

      {/* Opt-In/Out Toggle Confirmation */}
      <ConfirmDialog
        isOpen={showOptToggleConfirm}
        onClose={() => {
          setShowOptToggleConfirm(false);
          setContactToToggle(null);
        }}
        onConfirm={confirmToggleOptIn}
        title={contactToToggle?.optedIn ? 'Opt Out Contact' : 'Opt In Contact'}
        description={
          contactToToggle?.optedIn
            ? `Are you sure you want to opt out ${contactToToggle?.name || contactToToggle?.phoneNumber}? They will no longer receive messages from your campaigns.`
            : `Are you sure you want to opt in ${contactToToggle?.name || contactToToggle?.phoneNumber}? They will start receiving messages from your campaigns.`
        }
        confirmText={contactToToggle?.optedIn ? 'Opt Out' : 'Opt In'}
        variant={contactToToggle?.optedIn ? 'warning' : 'success'}
        isLoading={isTogglingOpt}
      />

      {/* Import Contacts Modal */}
      <ImportContactsModal
        isOpen={showImportModal}
        onClose={() => setShowImportModal(false)}
        onSuccess={() => {
          setShowImportModal(false);
          fetchContacts();
          fetchStats();
          fetchTags();
        }}
        groups={groups}
        organizationId={selectedOrganizationId}
      />

      {/* Export Contacts Modal */}
      <ExportContactsModal
        isOpen={showExportModal}
        onClose={() => setShowExportModal(false)}
        groups={groups}
        selectedTags={selectedTags}
        selectedGroupIds={selectedGroupIds}
        optedInFilter={optedInFilter}
        totalContacts={stats?.total || 0}
      />
    </div>
  );
};

export default ContactsPage;

